import React, { useMemo, useState } from "react";
import DashboardMrtTable from "./DashboardMrtTable";
import { DASHBOARD_EMPHASIS_CELL_SX } from "./dashboardTableStyles";
import TaskDetailsModal from "../TaskDetailsModal";
import AppDrawer from "../common/AppDrawer";

const STATUS_STYLES = {
  completed: { background: "#dcfce7", color: "#166534" },
  wip: { background: "#dbeafe", color: "#1e40af" },
  pending: { background: "#fef9c3", color: "#854d0e" },
};

const PRIORITY_COLORS = {
  urgent: "#ef4444",
  critical: "#ef4444",
  high: "#f97316",
  medium: "#f59e0b",
  low: "#9ca3af",
};

const StatusBadge = ({ status = "" }) => {
  const key = status?.toLowerCase();
  const style = STATUS_STYLES[key] ?? { background: "#f3f4f6", color: "#374151" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        borderRadius: "999px",
        fontSize: "0.75rem",
        fontWeight: 600,
        ...style,
      }}
    >
      {status || "—"}
    </span>
  );
};

const PriorityCell = ({ priority = "" }) => {
  const color = PRIORITY_COLORS[priority?.toLowerCase()] ?? "#9ca3af";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: "0.875rem", color: "#374151", textTransform: "capitalize" }}>
        {priority || "—"}
      </span>
    </span>
  );
};

const MyTasksTable = ({ rows = [], isLoading = false, totalItems = 0, currentPage = 1, pageSize = 10, onPaginationChange, sorting = [], onSortingChange }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const handleTicketClick = (row) => {
    // Pass minimal task shape — TaskDetailsModal fetches full details using task.id
    setSelectedTask({ id: row.id });
    setDrawerOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "ticketId",
        header: "ID",
        size: 120,
        Cell: ({ row }) => {
          const ticketId = row.original.ticketId || row.original.id;
          return ticketId ? (
            <span
              onClick={() => handleTicketClick(row.original)}
              style={{
                fontWeight: 600,
                color: "#2563eb",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              {ticketId}
            </span>
          ) : (
            <span style={{ color: "#9ca3af" }}>—</span>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Task",
        size: 220,
        Cell: ({ row }) => (
          <span style={{ fontWeight: 600, color: "#111827", fontSize: "0.875rem" }}>
            {row.original.name}
          </span>
        ),
      },
      {
        accessorKey: "tag",
        header: "Project",
        size: 150,
        Cell: ({ row }) => (
          <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>{row.original.tag}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 110,
        Cell: ({ row }) => <PriorityCell priority={row.original.priority} />,
      },
      {
        accessorKey: "due",
        header: "Due",
        size: 100,
      },
      {
        accessorKey: "overdue",
        header: "Overdue",
        size: 90,
        Cell: ({ row }) => {
          const val = row.original.overdue;
          const isOverdue = val && val !== "—";
          return (
            <span style={{ fontWeight: isOverdue ? 700 : 400, color: isOverdue ? "#ef4444" : "#9ca3af" }}>
              {val ?? "—"}
            </span>
          );
        },
      },
      {
        accessorKey: "age",
        header: "Age",
        size: 80,
      },
      {
        accessorKey: "hrs",
        header: "Hrs Logged",
        size: 100,
        muiTableBodyCellProps: { sx: DASHBOARD_EMPHASIS_CELL_SX },
        Cell: ({ row }) => {
          const val = row.original.hrs;
          const isEmpty = !val || val === "0h";
          return (
            <span style={{ color: isEmpty ? "#ef4444" : "#111827", fontWeight: isEmpty ? 600 : 400 }}>
              {isEmpty ? "not logged" : val}
            </span>
          );
        },
      },
      {
        accessorKey: "last",
        header: "Last Active",
        size: 110,
      },
    ],
    []
  );

  return (
    <>
      <DashboardMrtTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        emptyMessage="No tasks found."
        unitName="tasks"
        scrollContainer={true}
        enablePagination={true}
        enableServerSide={true}
        rowCount={totalItems}
        state={{
          pagination: { pageIndex: Math.max(0, currentPage - 1), pageSize },
          sorting
        }}
        onPaginationChange={(updater) => {
          const newState = typeof updater === "function" ? updater({ pageIndex: Math.max(0, currentPage - 1), pageSize }) : updater;
          if (onPaginationChange) {
            onPaginationChange({ page: newState.pageIndex + 1, limit: newState.pageSize });
          }
        }}
        onSortingChange={onSortingChange}
      />

      <AppDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedTask?.ticketId || "Task Details"}
      >
        <TaskDetailsModal
          task={selectedTask}
          variant="drawer"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      </AppDrawer>
    </>
  );
};

export default MyTasksTable;
